// @ts-check
// "Escolha as mensagens" ao usar um modelo de fluxo (spec 2.0.0, fluxos.md seção 20.3, item 5; contrato 09 de fluxos,
// seção 17): uma linha por mensagem do modelo. E-mail: o modelo pronto (a área de e-mail cria a cópia), um modelo da
// escola ou "Escolher depois". WhatsApp: número e modelo aprovado, ou "Escolher depois", com o texto sugerido e o atalho
// "Criar modelo" em outra aba (o modelo depende da aprovação da Meta).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { EmailTemplatePicker } from '../email/email-template-picker.js';
import { WhatsappTemplateGroupedPicker } from '../whatsapp-campaigns/whatsapp-template-grouped-picker.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { FlowDialog } from './flow-dialog.js';
import { FlowRadioOption } from './flow-lifecycle-dialogs.js';
import { FlowField } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/flows.js').FlowTemplateItem} FlowTemplateItem
 * @typedef {import('../../data/flows.js').FlowTemplateChoice} FlowTemplateChoice
 * @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate
 * @typedef {import('../../data/conversations.js').Channel} Channel
 * @typedef {{ mode: 'GALLERY' | 'MINE' | 'LATER' | 'PICK', templateId: string | null, templateName?: string | null, channelId: string | null }} ChoiceState
 */

/** Escolhas no formato da API (`choices[nodeKey]`). @param {FlowTemplateItem} template @param {Record<string, ChoiceState>} states */
export function templateChoicesPayload(template, states) {
  /** @type {Record<string, Record<string, unknown>>} */
  const payload = {};
  for (const choice of template.choices) {
    const state = states[choice.nodeKey];
    if (!state || state.mode === 'LATER') payload[choice.nodeKey] = { later: true };
    else if (state.mode === 'GALLERY') payload[choice.nodeKey] = { useGallery: true };
    else if (choice.kind === 'EMAIL') payload[choice.nodeKey] = state.templateId ? { templateId: state.templateId } : { later: true };
    else payload[choice.nodeKey] = state.templateId ? { templateId: state.templateId, channelId: state.channelId } : { later: true };
  }
  return payload;
}

/** Estado inicial: e-mail com o modelo pronto, WhatsApp para escolher. @param {FlowTemplateItem} template */
export function initialChoiceStates(template) {
  return Object.fromEntries(template.choices.map((choice) => [choice.nodeKey, /** @type {ChoiceState} */ ({
    mode: choice.kind === 'EMAIL' && choice.emailGalleryKey ? 'GALLERY' : 'LATER', templateId: null, channelId: null,
  })]));
}

/**
 * @param {{ template: FlowTemplateItem, whatsappTemplates: WhatsappTemplate[] | null, channels: Channel[] | null, onBack: () => void,
 *   onCreate: (choices: Record<string, Record<string, unknown>>) => Promise<boolean | void>, onClose: () => void }} props
 */
export function FlowTemplateChoicesDialog({ template, whatsappTemplates, channels, onBack, onCreate, onClose }) {
  const [states, setStates] = useState(() => initialChoiceStates(template));
  const [isBusy, setIsBusy] = useState(false);
  const [openPicker, setOpenPicker] = useState(/** @type {string | null} */ (null));
  /** @param {string} nodeKey @param {Partial<ChoiceState>} patch */
  const update = (nodeKey, patch) => setStates((current) => ({ ...current, [nodeKey]: { ...current[nodeKey], ...patch } }));
  const create = async () => {
    setIsBusy(true);
    const result = await onCreate(templateChoicesPayload(template, states));
    if (result === false) setIsBusy(false);
  };
  const canUseMarketing = hasCurrentPermissionFlag('WHATSAPP_CAMPAIGNS');
  const activeChannels = (channels ?? []).filter((channel) => channel.isActive !== false);
  const returnPath = `${window.location.pathname}${window.location.search}`;

  return html`<${FlowDialog} title=${t('flows.templates.choices.title')} subtitle=${t('flows.templates.choices.subtitle')} size="medium" onClose=${onClose}
    testId="flow-template-choices"
    footer=${html`<${Button} title=${t('flows.templates.choices.back')} variant="secondary" size="small" onClick=${onBack} />
      <${Button} title=${t('flows.templates.choices.create')} accent="blue" size="small" isLoading=${isBusy} onClick=${create} />`}>
    <ol class="flow-choices-list">
      ${template.choices.map((choice) => {
        const state = states[choice.nodeKey];
        const name = `flow-choice-${choice.nodeKey}`;
        return html`<li key=${choice.nodeKey} class="flow-choice">
          <div class="flow-choice-header">
            <span class="flow-choice-icon" data-kind=${choice.kind} aria-hidden="true"><${Icon} name=${choice.kind === 'EMAIL' ? 'IconMail' : 'IconBrandWhatsapp'} size=${16} /></span>
            <span class="flow-choice-label">${t(choice.labelKey)}</span>
          </div>
          ${choice.kind === 'EMAIL'
            ? html`<div class="flow-radio-group" role="radiogroup" aria-label=${t(choice.labelKey)}>
                ${choice.emailGalleryKey
                  ? html`<${FlowRadioOption} name=${name} value="GALLERY" checked=${state.mode === 'GALLERY'} title=${t('flows.templates.choices.useReady')}
                      description=${t('flows.templates.choices.useReadyHint')} onSelect=${() => update(choice.nodeKey, { mode: 'GALLERY' })} />`
                  : null}
                <${FlowRadioOption} name=${name} value="MINE" checked=${state.mode === 'MINE'} title=${t('flows.templates.choices.pickMine')}
                  description=${state.mode === 'MINE' && state.templateName ? state.templateName : null}
                  onSelect=${() => {
                    update(choice.nodeKey, { mode: 'MINE' });
                    setOpenPicker(choice.nodeKey);
                  }} />
                <${FlowRadioOption} name=${name} value="LATER" checked=${state.mode === 'LATER'} title=${t('flows.templates.choices.later')}
                  onSelect=${() => update(choice.nodeKey, { mode: 'LATER' })} />
                ${state.mode === 'MINE' && openPicker === choice.nodeKey
                  ? html`<div class="flow-choice-picker">
                      <${EmailTemplatePicker} value=${state.templateId} returnPath=${returnPath}
                        onChange=${(/** @type {{ templateId: string, name: string }} */ picked) => {
                          update(choice.nodeKey, { templateId: picked.templateId, templateName: picked.name });
                          setOpenPicker(null);
                        }} />
                    </div>`
                  : state.mode === 'MINE'
                    ? html`<button type="button" class="flow-link-button" onClick=${() => setOpenPicker(choice.nodeKey)}>${t('flows.templates.choices.change')}</button>`
                    : null}
              </div>`
            : html`<div class="flow-radio-group" role="radiogroup" aria-label=${t(choice.labelKey)}>
                <${FlowRadioOption} name=${name} value="PICK" checked=${state.mode === 'PICK'} title=${t('flows.templates.choices.pickWhatsapp')}
                  description=${state.mode === 'PICK' && state.templateName ? state.templateName : null}
                  onSelect=${() => update(choice.nodeKey, { mode: 'PICK', channelId: state.channelId ?? activeChannels[0]?.id ?? null })} />
                <${FlowRadioOption} name=${name} value="LATER" checked=${state.mode === 'LATER'} title=${t('flows.templates.choices.later')}
                  onSelect=${() => update(choice.nodeKey, { mode: 'LATER' })} />
                ${state.mode === 'PICK'
                  ? html`<div class="flow-choice-picker">
                      ${activeChannels.length === 0
                        ? html`<p class="flow-field-hint">${t('flows.forms.whatsapp.noChannels')}</p>`
                        : html`<${FlowField} label=${t('flows.forms.whatsapp.channel')} htmlFor=${`${name}-channel`}>
                            <select id=${`${name}-channel`} class="text-input" value=${state.channelId ?? ''}
                              onChange=${(/** @type {Event} */ event) => update(choice.nodeKey, { channelId: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null, templateId: null, templateName: null })}>
                              ${activeChannels.map((channel) => html`<option key=${channel.id} value=${channel.id}>${channel.displayName ? `${channel.displayName} (${channel.handle})` : channel.handle}</option>`)}
                            </select>
                          <//>
                          <${WhatsappTemplateGroupedPicker} context="FLOW" selectedId=${state.templateId}
                            templates=${whatsappTemplates === null ? null : whatsappTemplates.filter((item) => !state.channelId || item.channelId === state.channelId)}
                            disabledReason=${(/** @type {WhatsappTemplate} */ item) => (item.category === 'MARKETING' && !canUseMarketing ? t('flows.forms.whatsapp.marketingNeedsFlag') : null)}
                            onSelect=${(/** @type {WhatsappTemplate} */ item) => update(choice.nodeKey, { templateId: item.id, templateName: item.name, channelId: item.channelId })} />`}
                    </div>`
                  : null}
                ${choice.whatsappHint
                  ? html`<div class="flow-choice-hint">
                      <span class="flow-choice-hint-title">${t(`flows.templates.choices.suggested.${choice.whatsappHint.category}`)}</span>
                      <p class="flow-choice-example">${choice.whatsappHint.bodyExample}</p>
                      <a class="flow-link-button" href=${`/campaigns/templates/whatsapp/new?from=${encodeURIComponent(returnPath)}`} target="_blank" rel="noopener">
                        <${Icon} name="IconExternalLink" size=${14} /> ${t('flows.templates.choices.createWhatsapp')}
                      </a>
                    </div>`
                  : null}
              </div>`}
        </li>`;
      })}
    </ol>
  <//>`;
}
